<template>
  <component :is="embedded ? 'div' : 'v-main'">
    <component :is="embedded ? 'div' : 'v-container'" v-bind="embedded ? {} : { fluid: true }">
      <v-card :elevation="embedded ? 0 : 6" :rounded="embedded ? undefined : 'xxl'">
        <!-- Embedded (dialog) use gets its banner header from AppDialog instead. -->
        <v-card-title v-if="!embedded">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >Add a Store
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="6">
              <v-text-field
                v-model="form.name"
                outlined
                prepend-inner-icon="mdi-store"
                :error-messages="errors.name"
                @input="form.name = form.name.toUpperCase()"
              >
                <template v-slot:label>Store Name <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="6">
              <v-textarea
                v-model="form.address"
                outlined
                prepend-inner-icon="mdi-map-marker"
                :error-messages="errors.address"
                rows="3"
                cols="3"
                auto-grow
              >
                <template v-slot:label>Address <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-textarea>
            </v-col>

            <v-col sm="3">
              <v-select
                v-model="form.country_id"
                :items="countries"
                item-text="description"
                item-value="id"
                outlined
                prepend-inner-icon="mdi-earth"
                :error-messages="errors.country_id"
                @change="onCountryChange"
              >
                <template v-slot:label>Country <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-select>
            </v-col>
            <v-col sm="3">
              <v-select
                v-model="form.state_id"
                :items="states"
                item-text="description"
                item-value="id"
                outlined
                prepend-inner-icon="mdi-map-marker-radius"
                :error-messages="errors.state_id"
                :disabled="!form.country_id"
                @change="onStateChange"
              >
                <template v-slot:label>State <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-select>
            </v-col>
            <v-col sm="3">
              <v-select
                v-model="form.city_id"
                :items="cities"
                item-text="description"
                item-value="id"
                outlined
                prepend-inner-icon="mdi-city"
                :error-messages="errors.city_id"
                :disabled="!form.state_id"
              >
                <template v-slot:label>City <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-select>
            </v-col>
            <v-col sm="3">
              <v-text-field
                v-model="form.pin_code"
                outlined
                prepend-inner-icon="mdi-mailbox"
                :error-messages="errors.pin_code"
              >
                <template v-slot:label>Pin Code <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="6">
              <label for="" class="mb-2 d-block">Store Image</label>

              <!-- File Input -->
              <input
                type="file"
                id="image_path"
                name="image_path"
                ref="image_path"
                accept="image/*"
                @change="previewImage"
                class="mt-2"
              />

              <!-- Remove button (only when image is selected) -->
              <v-btn
                v-if="imagePreview"
                x-small
                class="my-2"
                color="red"
                dark
                @click="removeImage"
              >
                Remove
              </v-btn>

              <!-- Image Preview -->
              <div v-if="imagePreview" class="mt-3">
                <v-avatar size="100">
                  <img :src="imagePreview" alt="Store Image Preview" />
                </v-avatar>
              </div>
            </v-col>

            <v-col sm="6">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.is_active"
                inset
                :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
              ></v-switch>
            </v-col>
          </v-row>
          <v-divider class="mt-5 mb-5"> </v-divider>
          <v-btn text class="mr-2" @click="embedded ? $emit('close') : $router.back()">Cancel</v-btn>
          <v-btn
            :loading="isLoading"
            :disabled="isLoading"
            rounded
            color="primary"
            dark
            @click="save"
          >
            Save Store
          </v-btn>
        </v-card-text>
      </v-card>
    </component>
  </component>
</template>
<script>
import axios from "axios";
export default {
  name: "StoreCreatePage",
  props: {
    // When true, this form is rendered inside another page's dialog (e.g.
    // the Stores list's inline "Add Store") instead of its own routed
    // page - it emits `created`/`close` rather than navigating away.
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      form: {
        country_id: null,
        state_id: null,
        city_id: null,
        is_active: true,
      },
      countries: [],
      states: [],
      cities: [],
      isLoading: false,
      imagePreview: null,
      store: null,
    };
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
  },
  mounted() {
    this.$store.dispatch("errors/setErrors", {});
    this.form.company_id = this.company.id;
    this.fetchCountries();
  },
  methods: {
    async fetchCountries() {
      try {
        const response = await axios.get("/location-countries");
        this.countries = response.data.data || [];
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    },
    async onCountryChange() {
      this.form.state_id = null;
      this.form.city_id = null;
      this.states = [];
      this.cities = [];

      if (this.form.country_id) {
        try {
          const response = await axios.get("/location-states", {
            params: { country_id: this.form.country_id },
          });
          this.states = response.data.data || [];
        } catch (error) {
          console.error("Error fetching states:", error);
        }
      }
    },
    async onStateChange() {
      this.form.city_id = null;
      this.cities = [];

      if (this.form.state_id) {
        try {
          const response = await axios.get("/location-cities", {
            params: { state_id: this.form.state_id },
          });
          this.cities = response.data.data || [];
        } catch (error) {
          console.error("Error fetching cities:", error);
        }
      }
    },
    previewImage(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    removeImage() {
      this.$refs.image_path.value = null;
      this.imagePreview = null;
    },
    async save() {
      try {
        this.isLoading = true;
        this.$store.dispatch("errors/setErrors", {});
        let response = await axios.post("/stores", this.form);
        this.store = response.data.data;
        await this.handleFileUpload();
        this.isLoading = false;
        if (this.embedded) {
          this.$emit("created", this.store);
        } else {
          this.$router.push("/stores");
        }
      } catch (e) {
        console.log(e);
        this.isLoading = false;
        if (e.response && e.response.data && e.response.data.errors) {
          this.$store.dispatch("errors/setErrors", e.response.data.errors);
        }
      }
    },
    async handleFileUpload() {
      if (!this.$refs.image_path.files[0]) return;

      let formData = new FormData();
      formData.append("id", this.store.id);
      formData.append("image_path", this.$refs.image_path.files[0]);

      await axios
        .post("upload_stores_image", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .catch((error) => {
          console.log(error);
        });
    },
  },
};
</script>
